/**
 * Tonnie Brand Assets
 *
 * Logos, favicons, and app icons for the Tonnie platform.
 * These assets are used across all portals.
 */

import type { AssetPath, LogoVariant } from '../types';

/**
 * Base path for brand assets
 */
const BRAND_BASE = '/assets/images/brand';

/**
 * Extended logo variant type including all available variants
 */
export type ExtendedLogoVariant =
  | LogoVariant
  | 'symbolFull'
  | 'symbolWhite'
  | 'verticalBlack'
  | 'verticalFull'
  | 'verticalWhite';

/**
 * Brand logo assets (core variants)
 */
export const BRAND_LOGOS: Record<LogoVariant, AssetPath> = {
  horizontal: {
    path: `${BRAND_BASE}/tonnie-logo-horizontal.png`,
    alt: 'Tonnie Logo',
    width: 160,
    height: 40,
  },
  symbol: {
    path: `${BRAND_BASE}/tonnie-logo-symbol.png`,
    alt: 'Tonnie Symbol',
    width: 48,
    height: 48,
  },
  wordmark: {
    path: `${BRAND_BASE}/logo-wordmark.svg`,
    alt: 'Tonnie Wordmark',
    width: 120,
    height: 32,
  },
} as const;

/**
 * Extended logo variants (additional color/style options)
 */
export const BRAND_LOGOS_EXTENDED: Record<ExtendedLogoVariant, AssetPath> = {
  ...BRAND_LOGOS,
  symbolFull: {
    path: `${BRAND_BASE}/tonnie-logo-symbol-full.png`,
    alt: 'Tonnie Symbol (Full Color)',
    width: 48,
    height: 48,
  },
  symbolWhite: {
    path: `${BRAND_BASE}/tonnie-logo-symbol-white.png`,
    alt: 'Tonnie Symbol (White)',
    width: 48,
    height: 48,
  },
  verticalBlack: {
    path: `${BRAND_BASE}/tonnie-logo-vertical-black.png`,
    alt: 'Tonnie Logo Vertical (Black)',
    width: 120,
    height: 160,
  },
  verticalFull: {
    path: `${BRAND_BASE}/tonnie-logo-vertical-full.png`,
    alt: 'Tonnie Logo Vertical (Full Color)',
    width: 120,
    height: 160,
  },
  verticalWhite: {
    path: `${BRAND_BASE}/tonnie-logo-vertical-white.png`,
    alt: 'Tonnie Logo Vertical (White)',
    width: 120,
    height: 160,
  },
} as const;

/**
 * Favicon and app icons
 */
export const BRAND_ICONS = {
  favicon: {
    path: `${BRAND_BASE}/favicon.svg`,
    alt: 'Tonnie Favicon',
    width: 32,
    height: 32,
  },
  faviconPng: {
    path: `${BRAND_BASE}/favicon.png`,
    alt: 'Tonnie Favicon',
    width: 32,
    height: 32,
  },
  appIcon: {
    path: `${BRAND_BASE}/app-icon.png`,
    alt: 'Tonnie App Icon',
    width: 1024,
    height: 1024,
  },
  splashIcon: {
    path: `${BRAND_BASE}/splash-icon.png`,
    alt: 'Tonnie Splash Icon',
    width: 1024,
    height: 1024,
  },
} as const;

/**
 * Android adaptive icons
 */
export const ANDROID_ICONS = {
  background: {
    path: `${BRAND_BASE}/android/android-icon-background.png`,
    alt: 'Android Icon Background',
    width: 108,
    height: 108,
  },
  foreground: {
    path: `${BRAND_BASE}/android/android-icon-foreground.png`,
    alt: 'Android Icon Foreground',
    width: 108,
    height: 108,
  },
  monochrome: {
    path: `${BRAND_BASE}/android/android-icon-monochrome.png`,
    alt: 'Android Icon Monochrome',
    width: 108,
    height: 108,
  },
} as const;

/**
 * All brand assets combined
 */
export const BRAND_ASSETS = {
  logo: {
    horizontal: BRAND_LOGOS.horizontal.path,
    symbol: BRAND_LOGOS.symbol.path,
    wordmark: BRAND_LOGOS.wordmark.path,
    symbolFull: BRAND_LOGOS_EXTENDED.symbolFull.path,
    symbolWhite: BRAND_LOGOS_EXTENDED.symbolWhite.path,
    verticalBlack: BRAND_LOGOS_EXTENDED.verticalBlack.path,
    verticalFull: BRAND_LOGOS_EXTENDED.verticalFull.path,
    verticalWhite: BRAND_LOGOS_EXTENDED.verticalWhite.path,
  },
  favicon: BRAND_ICONS.favicon.path,
  faviconPng: BRAND_ICONS.faviconPng.path,
  appIcon: BRAND_ICONS.appIcon.path,
  splashIcon: BRAND_ICONS.splashIcon.path,
  android: ANDROID_ICONS,
} as const;

/**
 * Get logo path by variant
 */
export function getBrandLogo(variant: LogoVariant = 'horizontal'): string {
  return BRAND_LOGOS[variant].path;
}

/**
 * Get extended logo path by variant
 */
export function getBrandLogoExtended(variant: ExtendedLogoVariant = 'horizontal'): string {
  return BRAND_LOGOS_EXTENDED[variant].path;
}

/**
 * Get logo asset with metadata
 */
export function getBrandLogoAsset(variant: LogoVariant = 'horizontal'): AssetPath {
  return BRAND_LOGOS[variant];
}

/**
 * Get extended logo asset with metadata
 */
export function getBrandLogoAssetExtended(variant: ExtendedLogoVariant = 'horizontal'): AssetPath {
  return BRAND_LOGOS_EXTENDED[variant];
}
